import { forwardRef } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";

interface FindNearbyButtonProps extends ButtonProps {
    /** Whether the nearby panel is currently expanded (shows the close icon). */
    isExpanded: boolean;
}

/**
 * "Find Nearby" action button, shared by the row layout (wrapped in a Radix
 * CollapsibleTrigger via `asChild`) and the grid layout (plain `onClick`).
 *
 * The close icon is absolutely positioned next to the centered label so it
 * never changes the button width (no card shift) or pulls the label off-center.
 */
export const FindNearbyButton = forwardRef<
    HTMLButtonElement,
    FindNearbyButtonProps
>(({ isExpanded, ...props }, ref) => (
    <Button
        ref={ref}
        variant="default"
        className="find-nearby-btn tw:relative"
        data-state={isExpanded ? "open" : "closed"}
        aria-expanded={isExpanded}
        {...props}
    >
        <span className="tw:relative">
            Find Nearby
            {isExpanded && (
                <svg
                    className="find-nearby-btn__close tw:absolute tw:left-full tw:top-1/2 tw:-translate-y-1/2 tw:ml-2 tw:w-3.5 tw:h-3.5"
                    viewBox="0 0 14 14"
                    aria-hidden="true"
                >
                    <path
                        d="M1 1l12 12M13 1L1 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                </svg>
            )}
        </span>
    </Button>
));

FindNearbyButton.displayName = "FindNearbyButton";
